import type { ReactNode } from "react";
import type { Content } from "@/app/queries";
import { DashedButton, IconButton, INPUT, type Patch } from "./shared";

type Stuck = Content["stuck"];

/** «Если застрял»: оценка времени, что перечитать, подсказки по одной, частые ошибки.
 *  Студент видит свёрнутый Callout kind=tip; в гейт не входит. */
export function StuckEditor({ content, patch }: { content: Content; patch: (p: Patch) => void }) {
  const stuck = content.stuck;
  const set = (fn: (s: Stuck) => Stuck) => patch((c) => ({ ...c, stuck: fn(c.stuck) }));
  return (
    <section className="overflow-hidden rounded-md border border-bd bg-card" style={{ borderLeft: "2px solid var(--acc)" }} data-testid="block-stuck">
      <div className="flex items-center gap-2.5 border-b border-bd px-3.5 py-[11px]">
        <span className="font-mono text-[11px] tracking-[.05em] text-acc">подсказки</span>
        <span className="text-[13.5px] font-semibold">Если застрял</span>
        <span className="flex-1" />
        <label className="flex cursor-pointer items-center gap-[7px]">
          <input type="checkbox" checked={stuck.on} onChange={(e) => set((s) => ({ ...s, on: e.target.checked }))} className="size-[15px] cursor-pointer accent-[var(--acc)]" />
          <span className="font-mono text-[11px]" style={{ color: stuck.on ? "var(--acc)" : "var(--mut)" }}>
            {stuck.on ? "показывать" : "скрыт"}
          </span>
        </label>
      </div>
      <div className="flex flex-col gap-3.5 px-3.5 py-3">
        <label className="flex flex-col gap-1">
          <span className="font-mono text-[10.5px] text-mut">сколько это обычно занимает</span>
          <input
            aria-label="сколько это обычно занимает"
            value={stuck.estimate ?? ""}
            placeholder="2–3 вечера на практику — это нормально"
            onChange={(e) =>
              set((s) => {
                const next = { ...s };
                if (e.target.value.trim() === "") delete next.estimate;
                else next.estimate = e.target.value;
                return next;
              })
            }
            className={`${INPUT} font-sans text-[13px]`}
          />
        </label>

        <Group title="что перечитать" n={stuck.prereqs.length} add="+ ссылка" onAdd={() => set((s) => ({ ...s, prereqs: [...s.prereqs, { title: "", href: "" }] }))}>
          {stuck.prereqs.map((l, i) => (
            <Row key={`p-${i}`} onRemove={() => set((s) => ({ ...s, prereqs: s.prereqs.filter((_, j) => j !== i) }))} removeLabel={`удалить ссылку ${i + 1}`}>
              <input
                aria-label={`название ссылки ${i + 1}`}
                value={l.title}
                placeholder="трек · шаг или ресурс"
                onChange={(e) => set((s) => ({ ...s, prereqs: s.prereqs.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) }))}
                className={`${INPUT} min-w-0 flex-1 font-sans text-[12.5px]`}
              />
              <input
                aria-label={`адрес ссылки ${i + 1}`}
                value={l.href}
                placeholder="/track/py или https://…"
                onChange={(e) => set((s) => ({ ...s, prereqs: s.prereqs.map((x, j) => (j === i ? { ...x, href: e.target.value } : x)) }))}
                className={`${INPUT} w-[200px] font-mono text-[11.5px]`}
              />
            </Row>
          ))}
        </Group>

        <Group title="подсказки" n={stuck.hints.length} add="+ подсказка" hint="студент открывает по одной: «показать подсказку 2 из 4»" onAdd={() => set((s) => ({ ...s, hints: [...s.hints, ""] }))}>
          {stuck.hints.map((h, i) => (
            <Row key={`h-${i}`} onRemove={() => set((s) => ({ ...s, hints: s.hints.filter((_, j) => j !== i) }))} removeLabel={`удалить подсказку ${i + 1}`}>
              <span className="w-4 flex-none pt-2 font-mono text-[11px] text-mut">{i + 1}</span>
              <textarea
                aria-label={`подсказка ${i + 1}`}
                rows={1}
                value={h}
                placeholder="подсказка к задачам практики"
                onChange={(e) => set((s) => ({ ...s, hints: s.hints.map((x, j) => (j === i ? e.target.value : x)) }))}
                className={`${INPUT} min-w-0 flex-1 resize-y font-sans text-[12.5px] leading-[1.5]`}
              />
            </Row>
          ))}
        </Group>

        <Group title="частые ошибки" n={stuck.errors.length} add="+ ошибка" onAdd={() => set((s) => ({ ...s, errors: [...s.errors, { t: "", d: "" }] }))}>
          {stuck.errors.map((m, i) => (
            <Row key={`e-${i}`} onRemove={() => set((s) => ({ ...s, errors: s.errors.filter((_, j) => j !== i) }))} removeLabel={`удалить ошибку ${i + 1}`}>
              <input
                aria-label={`ошибка ${i + 1}`}
                value={m.t}
                placeholder="что происходит"
                onChange={(e) => set((s) => ({ ...s, errors: s.errors.map((x, j) => (j === i ? { ...x, t: e.target.value } : x)) }))}
                className={`${INPUT} w-[220px] font-sans text-[12.5px]`}
              />
              <input
                aria-label={`почему ломает ${i + 1}`}
                value={m.d}
                placeholder="почему и как чинить"
                onChange={(e) => set((s) => ({ ...s, errors: s.errors.map((x, j) => (j === i ? { ...x, d: e.target.value } : x)) }))}
                className={`${INPUT} min-w-0 flex-1 font-sans text-[12.5px] text-ink2`}
              />
            </Row>
          ))}
        </Group>
      </div>
    </section>
  );
}

function Group({ title, n, add, hint, onAdd, children }: { title: string; n: number; add: string; hint?: string; onAdd: () => void; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline gap-2.5">
        <span className="font-mono text-[10.5px] text-mut">
          {title} · {n}
        </span>
        {hint && <span className="font-mono text-[10.5px] text-mut">{hint}</span>}
      </div>
      {children}
      <DashedButton className="w-fit" onClick={onAdd}>
        {add}
      </DashedButton>
    </div>
  );
}

function Row({ onRemove, removeLabel, children }: { onRemove: () => void; removeLabel: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      {children}
      <IconButton onClick={onRemove} aria-label={removeLabel} className="pt-2">
        ×
      </IconButton>
    </div>
  );
}
